import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { GlobalStorageProvider, useGlobalStorageContext } from './contexts/GlobalStorageContext';
import { PrayerPage } from './pages/PrayerPage';
import { QiblahPage } from './pages/QiblahPage';
import { SettingsPage } from './pages/SettingsPage';
import { MorePage } from './pages/MorePage';
import { TasbihPage } from './pages/TasbihPage';
import { ZakatPage } from './pages/ZakatPage';
import { NamesPage } from './pages/NamesPage';
import { MonthlyPage } from './pages/MonthlyPage';
import { MasjidPage } from './pages/MasjidPage';
import { HijriPage } from './pages/HijriPage';
import { EventsPage, KahfPage, DuasPage, StatsPage, InheritancePage, HalalPage, TrainsPage, AppInfoPage } from './pages/AdditionalPages';
import { SALAT_PDF_URL } from './config/constantConfig';
import { useHijriAdjustment } from './hooks/useHijriAdjustment';
import { formatHijriDate } from './utils/hijriDate';
import './App.css';

const queryClient = new QueryClient();

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { colors } = useTheme();
  
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <aside className="sidebar" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
      <div className="sidebar-header" style={{ borderColor: colors.border }}>
        <h2 style={{ color: colors.tint }}>Rukousajat</h2>
      </div>
      <nav className="sidebar-nav">
        <Link to="/" className={`sidebar-item ${isActive('/') ? 'active' : ''}`} style={{ color: isActive('/') ? colors.tint : colors.muted, backgroundColor: isActive('/') ? `${colors.tint}1A` : 'transparent' }}>
          <span className="nav-icon">🕌</span>
          <span>Prayer</span>
        </Link>
        <Link to="/qiblah" className={`sidebar-item ${isActive('/qiblah') ? 'active' : ''}`} style={{ color: isActive('/qiblah') ? colors.tint : colors.muted, backgroundColor: isActive('/qiblah') ? `${colors.tint}1A` : 'transparent' }}>
          <span className="nav-icon">🧭</span>
          <span>Qiblah</span>
        </Link>
        <Link to="/masjid" className={`sidebar-item ${isActive('/masjid') ? 'active' : ''}`} style={{ color: isActive('/masjid') ? colors.tint : colors.muted, backgroundColor: isActive('/masjid') ? `${colors.tint}1A` : 'transparent' }}>
          <span className="nav-icon">🕌</span>
          <span>Masjid</span>
        </Link>
        <Link to="/more" className={`sidebar-item ${isActive('/more') ? 'active' : ''}`} style={{ color: isActive('/more') ? colors.tint : colors.muted, backgroundColor: isActive('/more') ? `${colors.tint}1A` : 'transparent' }}>
          <span className="nav-icon">☰</span>
          <span>More</span>
        </Link>
        <Link to="/settings" className={`sidebar-item ${isActive('/settings') ? 'active' : ''}`} style={{ color: isActive('/settings') ? colors.tint : colors.muted, backgroundColor: isActive('/settings') ? `${colors.tint}1A` : 'transparent' }}>
          <span className="nav-icon">⚙️</span>
          <span>Settings</span>
        </Link>
      </nav>
    </aside>
  );
};

const Navigation: React.FC = () => {
  const location = useLocation();
  const { colors } = useTheme();
  
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <nav className="bottom-nav" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
      <Link to="/" className={`nav-item ${isActive('/') ? 'active' : ''}`} style={{ color: isActive('/') ? colors.tint : colors.tabIconDefault }}>
        <span className="nav-icon">🕌</span>
        <span className="nav-label">Prayer</span>
      </Link>
      <Link to="/qiblah" className={`nav-item ${isActive('/qiblah') ? 'active' : ''}`} style={{ color: isActive('/qiblah') ? colors.tint : colors.tabIconDefault }}>
        <span className="nav-icon">🧭</span>
        <span className="nav-label">Qiblah</span>
      </Link>
      <Link to="/masjid" className={`nav-item ${isActive('/masjid') ? 'active' : ''}`} style={{ color: isActive('/masjid') ? colors.tint : colors.tabIconDefault }}>
        <span className="nav-icon">🕌</span>
        <span className="nav-label">Masjid</span>
      </Link>
      <Link to="/more" className={`nav-item ${isActive('/more') ? 'active' : ''}`} style={{ color: isActive('/more') ? colors.tint : colors.tabIconDefault }}>
        <span className="nav-icon">☰</span>
        <span className="nav-label">More</span>
      </Link>
      <Link to="/settings" className={`nav-item ${isActive('/settings') ? 'active' : ''}`} style={{ color: isActive('/settings') ? colors.tint : colors.tabIconDefault }}>
        <span className="nav-icon">⚙️</span>
        <span className="nav-label">Settings</span>
      </Link>
    </nav>
  );
};

const AppContent: React.FC = () => {
  const { manualLocation, hijriAdjustment } = useGlobalStorageContext();
  const { colors } = useTheme();

  useHijriAdjustment();
  const now = new Date();
  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };
  
  return (
    <div className="app-container">
      <Sidebar />
      <header className="app-header" style={{ backgroundColor: colors.tint }}>
        <div className="header-content">
          <span className="city-name">{manualLocation?.city || 'Helsinki, Finland'}</span>
          <span className="dates">
            {formatDate(now)} • {formatHijriDate(now, hijriAdjustment || 0)}
          </span>
          <button onClick={() => window.open(SALAT_PDF_URL, '_blank')} className="pdf-btn" title="Download PDF">
            ⬇️ PDF
          </button>
        </div>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<PrayerPage />} />
          <Route path="/qiblah" element={<QiblahPage />} />
          <Route path="/masjid" element={<MasjidPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/more" element={<MorePage />} />
          <Route path="/tasbih" element={<TasbihPage />} />
          <Route path="/zakat" element={<ZakatPage />} />
          <Route path="/names" element={<NamesPage />} />
          <Route path="/monthly" element={<MonthlyPage />} />
          <Route path="/hijri" element={<HijriPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/kahf" element={<KahfPage />} />
          <Route path="/duas" element={<DuasPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/inheritance" element={<InheritancePage />} />
          <Route path="/halal" element={<HalalPage />} />
          <Route path="/trains" element={<TrainsPage />} />
          <Route path="/appinfo" element={<AppInfoPage />} />
        </Routes>
      </main>
      <Navigation />
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <GlobalStorageProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </GlobalStorageProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
